import {mxConstants, mxEvent, mxEventObject, mxResources, mxUtils,} from './mxGraphFactory';

import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import 'fomantic-ui-less/definitions/elements/loader.less';

import 'fomantic-ui-less/definitions/modules/transition.less';
import 'fomantic-ui-less/definitions/modules/tab.less';
import 'fomantic-ui-less/definitions/modules/dropdown.less';
import 'fomantic-ui-less/definitions/modules/search.less';
import 'fomantic-ui-less/definitions/modules/popup.less';
import 'fomantic-ui-less/definitions/modules/calendar.less';

import 'fomantic-ui-less/definitions/modules/transition';
import 'fomantic-ui-less/definitions/modules/tab';
import 'fomantic-ui-less/definitions/modules/dropdown';
import 'fomantic-ui-less/definitions/modules/search';
import 'fomantic-ui-less/definitions/modules/popup';
import 'fomantic-ui-less/definitions/modules/calendar';

import 'fomantic-ui-less/definitions/behaviors/api';

import {deepCopy} from './helper';
import {api} from '../Common/fetchWithTimeout';
import {parseJSON, stringifyJSON} from '../Common/json';
import {createLogger} from '../Common/logger';
import {fromWorkflowDefinition} from './workflow';
import {EditorUi} from './EditorUi';
import {Editor} from './Editor';
import {dataCopyValue} from '../Common/copyToClipboard';
import {javascriptHint} from '../Common/javaScriptHint';
import {UPDATE_CELL_VALUE} from './Graph';
import {urlData} from '../Common/urlData';
import {urlEncodedValue} from '../Common/urlEncodedValue';

const LOGGER = createLogger("GraphEditorSidebar");

const fieldErrorClassName = 'ui basic error large pointing prompt label';

const logUnableToLoadHelpError = error => LOGGER.error("Unable to load help context", error);

/**
 * Constructs a new sidebar for the given editor.
 */
export const Sidebar = function (editorUi, container, currentWorkflowDefinition) {
    this.editorUi = editorUi;
    this.container = container;
    this.activeTab = null;
    this.currentWorkflowDefinition = currentWorkflowDefinition;

    this.init();
};

Sidebar.prototype.init = function () {
    const graph = this.editorUi.editor.graph;

    this.update = mxUtils.bind(this, function (sender, evt) {
        if (this.editorUi.currentMenu != null) {
            this.editorUi.hideCurrentMenu();
        }

        this.clearSelectionState();
        this.refresh();
    });

    graph.getSelectionModel().addListener(mxEvent.CHANGE, this.update);
    graph.addListener(mxEvent.EDITING_STARTED, this.update);
    graph.addListener(mxEvent.EDITING_STOPPED, this.update);
    graph.getModel().addListener(mxEvent.CHANGE, this.update);
    graph.addListener(mxEvent.ROOT, mxUtils.bind(this, function () {
        this.refresh();
    }));
};

Sidebar.prototype.clear = function () {
    this.container.innerHTML = '';

    if (this.panels != null) {
        for (let i = 0; i < this.panels.length; i++) {
            this.panels[i].destroy();
        }
    }

    this.panels = [];
};

const unableToLoadEditor = id => {
    const editorEl = document.getElementById(id);
    if (!editorEl) {
        return;
    }

    const errorMessage = document.createElement('div');
    errorMessage.className = 'ui message error visible';
    errorMessage.textContent = mxResources.get('errorLoadingEditor');
    editorEl.replaceWith(errorMessage);
}

const prepareHttpTaskErrors = taskErrors => {
    const errors = [];

    taskErrors?.fields?.credentials &&
    errors.push(taskErrors.fields.credentials);

    typeof taskErrors?.fields?.httpRequest === 'string' &&
    errors.push(taskErrors.fields.httpRequest);

    taskErrors?.fields?.httpRequest?.url &&
    errors.push(`url: ${taskErrors.fields.httpRequest.url}`);

    taskErrors?.fields?.httpRequest?.method &&
    errors.push(`method: ${taskErrors.fields.httpRequest.method}`);

    taskErrors?.fields?.connectionTimeoutSeconds &&
    errors.push(taskErrors.fields.connectionTimeoutSeconds);

    taskErrors?.fields?.readTimeoutSeconds &&
    errors.push(taskErrors.fields.readTimeoutSeconds);

    return errors;
}

const prepareEmailTaskErrors = taskErrors => {
    const errors = [];

    typeof taskErrors?.fields?.email === 'string' &&
    errors.push(taskErrors.fields.email);

    taskErrors?.fields?.email?.fromName &&
    errors.push(`fromName: ${taskErrors.fields.email.fromName}`);

    if (Array.isArray(taskErrors?.fields?.email?.toAddresses)) {
        taskErrors.fields.email.toAddresses.forEach(
            (message, index) => errors.push(
                `toAddresses[${index}]: ${message}`));
    } else if (taskErrors?.fields?.email?.toAddresses) {
        errors.push(`toAddresses: ${taskErrors.fields.email.toAddresses}`);
    }

    if (Array.isArray(taskErrors?.fields?.email?.ccAddresses)) {
        taskErrors.fields.email.ccAddresses.forEach(
            (message, index) => errors.push(
                `ccAddresses[${index}]: ${message}`));
    } else if (taskErrors?.fields?.email?.ccAddresses) {
        errors.push(`ccAddresses: ${taskErrors.fields.email.ccAddresses}`);
    }

    if (Array.isArray(taskErrors?.fields?.email?.bccAddresses)) {
        taskErrors.fields.email.bccAddresses.forEach(
            (message, index) => errors.push(
                `bccAddresses[${index}]: ${message}`));
    } else if (taskErrors?.fields?.email?.bccAddresses) {
        errors.push(`bccAddresses: ${taskErrors.fields.email.bccAddresses}`);
    }

    if (Array.isArray(taskErrors?.fields?.email?.replyToAddresses)) {
        taskErrors.fields.email.replyToAddresses.forEach(
            (message, index) => errors.push(
                `replyToAddresses[${index}]: ${message}`));
    } else if (taskErrors?.fields?.email?.replyToAddresses) {
        errors.push(
            `replyToAddresses: ${taskErrors.fields.email.replyToAddresses}`);
    }

    taskErrors?.fields?.email?.subject &&
    errors.push(`subject: ${taskErrors.fields.email.subject}`);

    taskErrors?.fields?.email?.html &&
    errors.push(`html: ${taskErrors.fields.email.html}`);

    taskErrors?.fields?.email?.text &&
    errors.push(`text: ${taskErrors.fields.email.text}`);

    if (Array.isArray(taskErrors?.fields?.email?.attachments)) {
        taskErrors.fields.email.attachments.forEach(
            (message, index) => errors.push(
                `attachments[${index}]: ${message}`));
    } else if (taskErrors?.fields?.email?.attachments) {
        errors.push(`attachments: ${taskErrors.fields.email.attachments}`);
    }

    return errors;
}

const handleWorkflowErrorHandlingTab = (container, model, workflow, currentWorkflowDefinition) => {
    const failureWorkflow = workflow.failureWorkflow;
    const failureWorkflowVersion = Number(workflow.failureWorkflowVersion) || 0;

    const failureWorkflowDropdown = $(container.querySelector('.ui.dropdown.failureWorkflow')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/workflow-definitions?size=1000',
            cache: false,
            rawResponse: true,
            onResponse: ({content: workflowDefinitions}) => ({
                success: true,
                results: workflowDefinitions.filter(item => !item.disabled && item.id !== currentWorkflowDefinition.id).map(entry => {
                    return {
                        value: entry.id,
                        name: entry.name,
                    };
                }),
            }),
        },
        onChange: value => {
            if (value !== failureWorkflow) {
                workflow.failureWorkflow = value;
                workflow.failureWorkflowVersion = null;
                model.setValue(model.getRoot(), workflow);
            }
        }
    });

    if (!isEmpty(failureWorkflow)) {
        failureWorkflowDropdown.dropdown('queryRemote', '', function () {
            failureWorkflowDropdown.dropdown('set selected', failureWorkflow);
        });

        const failureWorkflowVersionDropdown = $(container.querySelector('.ui.dropdown.failureWorkflowVersion')).dropdown({
            duration: 0,
            filterRemoteData: true,
            saveRemoteData: false,
            fullTextSearch: true,
            clearable: true,
            apiSettings: {
                url: `/api/workflow-definitions/${failureWorkflow}/versions`,
                cache: false,
                rawResponse: true,
                onResponse: summaryList => ({
                    success: true,
                    results: summaryList.map(summary => ({
                        value: summary.version,
                        name: `Version ${summary.version}`,
                    })),
                }),
            },
            onChange: value => {
                if (value !== failureWorkflowVersion) {
                    workflow.failureWorkflowVersion = value;
                    model.setValue(model.getRoot(), workflow);
                }
            }
        });

        failureWorkflowVersionDropdown.dropdown('queryRemote', '', function () {
            if (failureWorkflowVersion > 0) {
                failureWorkflowVersionDropdown.dropdown('set selected', failureWorkflowVersion);
            }
        });

        api(`/api/workflow-definition-graphs/${failureWorkflow}${failureWorkflowVersion > 0 ? `?version=${failureWorkflowVersion}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        }).then(workflowDefinition => {
            const graphContent = document.getElementById('failureWorkflowDiagram');
            const editorUi = new EditorUi(new Editor(false), graphContent,
                {
                    id: null,
                    version: 0,
                    currentVersion: 0,
                }, false);
            editorUi.editor.initializeWorkflow(workflowDefinition.graph || fromWorkflowDefinition(workflowDefinition.workflowDefinition, workflowDefinition.taskProcessors));
            editorUi.actions.get('save').setEnabled(false);
            graphContent.style.height = '400px';
            graphContent.style.display = '';
            editorUi.actions.get('resetView').funct();
        }).catch(error => LOGGER.error('Unable to fetch failure workflow definition', error));
    }
}

const handleTaskProcessorTab = (container, model, cell, task) => {
    const processorDropdown = $(container.querySelector('.ui.dropdown.processorPage')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/pages',
            cache: false,
            rawResponse: true,
            onResponse: pageTitles => ({
                success: true,
                results: pageTitles.map(pageTitle => {
                    let icon = 'user';
                    if (pageTitle.pageRestrictions === 'VIEW_PUBLIC') {
                        icon = 'globe';
                    } else if (pageTitle.pageRestrictions === 'PRIVATE') {
                        icon = 'lock';
                    }
                    return {
                        value: pageTitle.id,
                        name: pageTitle.path,
                        icon
                    }
                }),
            }),
        },
        onChange: value => {
            if (value !== processor) {
                task.processor = value;
                model.setValue(cell, task);
            }
        }
    });

    const processor = task.processor;
    if (!isEmpty(processor)) {
        processorDropdown.dropdown('queryRemote', '', function () {
            processorDropdown.dropdown('set selected', processor);
        });
    }
}

const handleWorkflowOutputTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                editor.on('blur', codeMirror => {
                    codeMirror.setOption('lint', !isEmpty(codeMirror.getValue()));

                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    const oldValue = task.workflowOutput;
                    const newValue = parseJSON(codeMirror.getValue());
                    if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                        task.workflowOutput = newValue;
                        model.setValue(cell, task);
                    }
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                document.getElementById('workflowOutputEditor').replaceWith(sidebar.createField({
                    id: 'workflowOutputEditor',
                    label: mxResources.get('workflowOutput'),
                    error: taskErrors?.fields?.workflowOutput,
                    fieldElement
                }));

                editor.refresh();
            },
            errorFn: () => unableToLoadEditor('workflowOutputEditor'),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'application/json',
                lint: !isEmpty(task.workflowOutput),
                value: task.workflowOutput ? stringifyJSON(task.workflowOutput, null, 2) : ''
            }
        })).catch(() => unableToLoadEditor('workflowOutputEditor'));
}

function handleInputParametersTab(container, editable, sidebar, model, cell, task, taskErrors) {
    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor').then(module => module.codeEditor({
        callbackFn: editor => {
            editor.on('blur', codeMirror => {
                codeMirror.setOption('lint', !isEmpty(codeMirror.getValue()));

                const getAnnotations = codeMirror.getHelper(
                    module.CodeMirror.Pos(0, 0), 'lint');
                const annotations = getAnnotations(codeMirror.getValue(),
                    codeMirror.state?.lint?.linterOptions, codeMirror);

                const oldValue = task.inputParameters;
                const newValue = parseJSON(codeMirror.getValue());
                if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                    task.inputParameters = newValue;
                    model.setValue(cell, task);
                }
            });

            const fieldElement = editor.getWrapperElement();
            fieldElement.classList.add('ui', 'segment');

            document.getElementById('inputParametersEditor').replaceWith(sidebar.createField({
                id: 'inputParametersEditor',
                label: mxResources.get('inputParameters'),
                error: taskErrors?.fields?.inputParameters,
                fieldElement
            }));

            import(/* webpackChunkName: "inputParametersHelp" */ "./help/inputParametersHelp")
                .then(module => {
                    if (!document.querySelector('#inputParametersEditor > details')) {
                        document.getElementById('inputParametersEditor').insertAdjacentHTML('beforeend', module.inputParametersHelp);
                    }
                })
                .catch(error => logUnableToLoadHelpError(error));

            editor.refresh();
        },
        errorFn: () => unableToLoadEditor('inputParametersEditor'),
        options: {
            readOnly: !editable ? 'nocursor' : false,
            mode: 'application/json',
            lint: !isEmpty(task.inputParameters),
            value: task.inputParameters ? stringifyJSON(task.inputParameters, null, 2) : ''
        }
    })).catch(() => unableToLoadEditor('inputParametersEditor'));
}

let credentialsRegionMap = {};
let credentialsOAuth2Map = {};

const handleHttpTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    document.getElementById('testHttpRequestField')?.remove();
    document.getElementById('testHttpResponse')?.remove();

    const credentialsDropdown = $(container.querySelector('.ui.dropdown.credentials')).dropdown({
        duration: 0,
        filterRemoteData: true,
        saveRemoteData: false,
        fullTextSearch: true,
        clearable: true,
        apiSettings: {
            url: '/api/credentials',
            cache: false,
            rawResponse: true,
            onResponse: credentialsList => ({
                success: true,
                results: credentialsList.map(entry => {
                    if (!isEmpty(entry.credentials.region)) {
                        credentialsRegionMap[entry.credentialsId] = entry.credentials.region;
                    }

                    if (entry.credentials.type === 'OAUTH2') {
                        credentialsOAuth2Map[entry.credentialsId] = entry.authorized;
                    }

                    const name = `${entry.credentials.name} (${mxResources.get(`credentials${entry.credentials.type}`)}${entry.credentials.type === 'AWS' ? ` - ${entry.credentials.region}` : ''}${entry.credentials.type === 'OAUTH2' ? (entry.authorized ? ' - Authorized' : ' - Not authorize') : ''})`;
                    return {
                        value: entry.credentialsId,
                        name,
                        text: name,
                        description: entry.credentials.description,
                        descriptionVertical: true
                    };
                }),
            })
        },
        onChange: value => {
            const credentials = task.credentials;
            if (value !== credentials) {
                task.credentials = value;
                model.setValue(cell, task);
            }
        }
    });

    const credentials = task.credentials;
    if (!isEmpty(credentials)) {
        credentialsDropdown.dropdown('queryRemote', '', function () {
            credentialsDropdown.dropdown('set selected', credentials);

            if (credentialsOAuth2Map.hasOwnProperty(credentials)) {
                container.querySelector('.ui.dropdown.credentials').insertAdjacentHTML('afterend', `<div class="ui top pointing basic large label"><a target="_blank" rel="noopener noreferrer" href="/credentials/${credentials}/authorize">${credentialsOAuth2Map[credentials] === true ? mxResources.get('reauthorizeCredentials', [credentials]) : mxResources.get('authorizeCredentials', [credentials])}</a></div>`);
            }
        });
    }

    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                editor.on('blur', codeMirror => {
                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    const oldValue = task.httpRequest;
                    const newValue = parseJSON(codeMirror.getValue());
                    if (newValue && !!Object.keys(newValue).length && !isEqual(oldValue, newValue) && isEmpty(annotations)) {
                        task.httpRequest = newValue;
                        model.setValue(cell, task);
                    }
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                document.getElementById('httpRequestEditor').replaceWith(sidebar.createField({
                    id: 'httpRequestEditor',
                    label: mxResources.get('httpRequest'),
                    className: 'required',
                    error: prepareHttpTaskErrors(taskErrors),
                    fieldElement
                }));

                import(/* webpackChunkName: "httpSettingsHelp" */ "./help/httpSettingsHelp")
                    .then(module => {
                        if (!document.querySelector('#httpRequestEditor > details')) {
                            document.getElementById('httpRequestEditor').insertAdjacentHTML('beforeend', module.httpSettingsHelp);
                        }
                    })
                    .catch(error => logUnableToLoadHelpError(error));

                document.querySelector('#httpRequestEditor > .CodeMirror')?.insertAdjacentHTML('beforebegin', `
                <div id="openApisSearch" class="ui short search field fluid">
                  <div class="ui left icon input">
                    <input class="prompt" type="text" placeholder="${mxResources.get('openApisSearch')}">
                    <i class="cogs icon"></i>
                  </div>
                </div>
                `);

                $(document.querySelector('#httpRequestEditor > #openApisSearch'))?.search({
                    type: 'category',
                    minCharacters: 1,
                    duration: 0,
                    transition: 'scale',
                    onSelect: result => {
                        const {servers, slug, apiPath} = result;

                        const server = servers[0];
                        let base = server.url;
                        server.variables?.forEach(variable => {
                            if (variable.name === 'region' && !isEmpty(credentialsRegionMap[task.credentials])) {
                                base = base.replace(`{${variable.name}}`, credentialsRegionMap[task.credentials]);
                                return;
                            }

                            base = base.replace(`{${variable.name}}`, `\${${variable.name}}`);
                        });

                        let path = apiPath.path;
                        apiPath.pathParameters?.forEach(parameter => {
                            path = path.replace(`{${parameter.name}}`, `\${${parameter.name}}`);
                        });

                        const requestURL = new URL(`${base?.replace(/\/+$/, '')?.replace(/[<\[]/, "${")?.replace(/[>\]]/, "}")}${path?.replace(/^\/+/, '/')}`);
                        apiPath.queryParameters?.forEach(parameter => {
                            if (parameter.required === true || !isEmpty(parameter.defaultValue)) {
                                requestURL.searchParams.append(parameter.name, isEmpty(parameter.defaultValue) ? `\${${parameter.name}}` : urlEncodedValue(parameter.defaultValue));
                            }
                        });

                        let isFormUrlencoded = false;
                        const headers = {};
                        apiPath.headerParameters?.forEach(parameter => {
                            if (parameter.name === 'Content-Type' && parameter.defaultValue === 'application/x-www-form-urlencoded') {
                                isFormUrlencoded = true;
                            }

                            if (parameter.required === true || !isEmpty(parameter.defaultValue)) {
                                headers[parameter.name] = isEmpty(parameter.defaultValue) ? `\${${parameter.name}}` : parameter.defaultValue;
                            }
                        });

                        const url = window.decodeURIComponent(requestURL.href);

                        cell.openApiSlug = slug;
                        task.httpRequest = {
                            url,
                            method: apiPath.method,
                            headers,
                            body: isFormUrlencoded ? window.decodeURIComponent($.param(apiPath.body)) : apiPath.body
                        };
                        model.setValue(cell, task);
                    },
                    apiSettings: {
                        url: '/api/search/open-apis?query={query}&size=100',
                        onResponse: ({content}) => {
                            const response = {results: {}};
                            $.each(content, function (index, item) {
                                const name = item.name || 'Unknown';
                                const maxResults = 10;

                                if (index >= maxResults) {
                                    return false;
                                }

                                if (response.results[name] === undefined) {
                                    response.results[name] = {
                                        name,
                                        results: [],
                                    };
                                }

                                response.results[name].results.push({
                                    title: `${item.apiPath.method} ${item.apiPath.path}`,
                                    description: item.apiPath.summary,
                                    image: `/images/logos/${item.logo}` || '',
                                    apiPath: item.apiPath,
                                    servers: item.servers,
                                    slug: item.slug
                                });
                            });
                            return response;
                        },
                    },
                });

                if (!isEmpty(cell.openApiSlug)) {
                    api(`/integration-operation/${cell.openApiSlug}?embed=true`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'text/html'
                        }
                    })
                        .then(pageContent => {
                            import(/* webpackPrefetch: true */ 'highlight.js/styles/idea.css');
                            document.querySelector('#httpRequestEditor > #openApisSearch')?.insertAdjacentHTML('beforeend', pageContent);
                        })
                        .catch(error => LOGGER.error("Unable to load integration page", error));
                }

                editor.refresh();

                document.getElementById('httpRequestEditor').insertAdjacentHTML('afterend', `<div id="testHttpRequestField" class="field"><button id="testHttpRequest" class="ui mini compact button">${mxResources.get('testRequest')}</button></div>`)
                document.getElementById('testHttpRequest')?.addEventListener('click', e => {
                    document.getElementById('testHttpResponse')?.remove();
                    $(e.target).api('destroy');

                    if (isEmpty(task.httpRequest?.url)) {
                        e.preventDefault();
                        return;
                    }

                    const credentials = task.credentials;
                    const {url, method, headers, body} = task.httpRequest || {};
                    const isSimWorkflowApi = url?.startsWith('/api/');

                    async function fetchOAuth2AuthorizeRequired() {
                        if (!isEmpty(credentials)) {
                            return await api(`/api/credentials/${credentials}`, {
                                method: 'GET',
                                headers: {
                                    'Accept': 'application/json'
                                }
                            })
                                .then(response => response?.credentials?.type === 'OAUTH2' && response?.authorized !== true)
                                .catch(() => false);
                        }

                        return false;
                    }

                    fetchOAuth2AuthorizeRequired().then(oAuth2AuthorizeRequired => {
                        if (oAuth2AuthorizeRequired) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(`/credentials/${credentials}/authorize`, '_blank').focus();
                            return false;
                        }

                        $(e.target).api({
                            cache: !isSimWorkflowApi,
                            url: isSimWorkflowApi ? url : '/api/proxy',
                            method: isSimWorkflowApi ? method : 'POST',
                            beforeXHR: function (xhr, settings) {
                                if (isSimWorkflowApi) {
                                    settings.data = body;

                                    if (!isEmpty(headers)) {
                                        Object.keys(headers).forEach(headerName => {
                                            xhr.setRequestHeader(headerName,
                                                headers[headerName]);
                                        });
                                    }
                                } else {
                                    settings.data = stringifyJSON({
                                        credentials,
                                        url: urlData(url, this),
                                        method,
                                        headers,
                                        body,
                                        input: task.inputParameters
                                    });
                                }
                                xhr.setRequestHeader('Content-Type', 'application/json');
                                xhr.setRequestHeader('Accept', 'application/json');
                                return xhr;
                            },
                            onComplete: function (response, element, xhr) {
                                const {status, statusText} = xhr;

                                document.getElementById('testHttpResponse')?.remove();

                                const jsonResponseField = document.createElement('div');
                                jsonResponseField.id = 'testHttpResponse';
                                jsonResponseField.className = 'field';
                                if (status === 0 || (status >= 200 && status < 400)) {
                                    jsonResponseField.insertAdjacentHTML('beforeend', `<label>${mxResources.get('httpResponse', [status, statusText])}</label>`);
                                } else {
                                    jsonResponseField.insertAdjacentHTML('beforeend', `<label><span class="ui error text">${mxResources.get('httpResponse', [status, statusText])}</span></label>`);
                                }

                                import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor').then(
                                    module => module.codeEditor({
                                        callbackFn: editor => {
                                            if (!isEmpty(response)) {
                                                editor.setValue(stringifyJSON(response, '', 2));
                                            } else {
                                                editor.setValue('');
                                            }

                                            jsonResponseField.appendChild(editor.getWrapperElement());

                                            document.getElementById('testHttpRequestField').insertAdjacentElement('afterend', jsonResponseField);

                                            editor.refresh();
                                        },
                                        errorFn: () => {
                                            jsonResponseField.insertAdjacentHTML('beforeend', `<pre class="ui container scrolling very short"><code>${stringifyJSON(response, '', 2)}</code></pre>`);
                                            document.getElementById('testHttpRequestField').insertAdjacentElement('afterend', jsonResponseField);
                                        },
                                        options: {
                                            mode: 'application/json',
                                            readOnly: true,
                                            foldGutter: true,
                                            lineWrapping: true,
                                            gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
                                        },
                                    }),
                                );
                            }
                        }).api('query');
                    });
                });
            },
            errorFn: () => unableToLoadEditor('httpRequestEditor'),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'application/json',
                lint: true,
                value: stringifyJSON(task.httpRequest || {
                    url: '',
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: null
                }, null, 2)
            }
        })).catch(() => unableToLoadEditor('httpRequestEditor'));
}

const handleEmailTab = (container, editable, sidebar, model, cell, task, taskErrors) => {
    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                editor.on('blur', codeMirror => {
                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    const oldValue = task.email;
                    const newValue = parseJSON(codeMirror.getValue());
                    if (newValue && !!Object.keys(newValue).length && !isEqual(oldValue, newValue) && isEmpty(annotations)) {
                        task.email = newValue;
                        model.setValue(cell, task);
                    }
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                document.getElementById('emailEditor').replaceWith(sidebar.createField({
                    id: 'emailEditor',
                    label: mxResources.get('email'),
                    className: 'required',
                    error: prepareEmailTaskErrors(taskErrors),
                    fieldElement
                }));

                import(/* webpackChunkName: "emailSettingsHelp" */ "./help/emailSettingsHelp")
                    .then(module => {
                        if (!document.querySelector('#emailEditor > details')) {
                            document.getElementById('emailEditor').insertAdjacentHTML('beforeend', module.emailSettingsHelp);
                        }
                    })
                    .catch(error => logUnableToLoadHelpError(error));

                editor.refresh();
            },
            errorFn: () => unableToLoadEditor('emailEditor'),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'application/json',
                lint: true,
                value: stringifyJSON(task.email || {
                    fromName: '',
                    toAddresses: [''],
                    ccAddresses: [],
                    bccAddresses: [],
                    replyToAddresses: [],
                    subject: '',
                    html: '',
                    text: null,
                    attachments: []
                }, null, 2)
            }
        })).catch(() => unableToLoadEditor('emailEditor'));
}

const handleRoutingTab = (container, editable, sidebar, model, cell, route, routeErrors) => {
    import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor')
        .then(module => module.codeEditor({
            callbackFn: editor => {
                editor.on('blur', codeMirror => {
                    const getAnnotations = codeMirror.getHelper(module.CodeMirror.Pos(0, 0), 'lint');
                    const annotations = getAnnotations(codeMirror.getValue(), codeMirror.state?.lint?.linterOptions, codeMirror);

                    const oldValue = route.condition;
                    const newValue = codeMirror.getValue().trim();
                    if (!isEqual(oldValue, newValue) && isEmpty(annotations)) {
                        route.condition = codeMirror.getValue().trim();
                        model.setValue(cell, route);
                    }
                });

                const fieldElement = editor.getWrapperElement();
                fieldElement.classList.add('ui', 'segment');

                document.getElementById('routingConditionEditor').replaceWith(sidebar.createField({
                    id: 'routingConditionEditor',
                    error: routeErrors?.fields?.condition,
                    fieldElement
                }));

                import(/* webpackChunkName: "conditionExpressionHelp" */ "./help/conditionExpressionHelp")
                    .then(module => {
                        if (!document.querySelector('#routingConditionEditor > details')) {
                            document.getElementById('routingConditionEditor').insertAdjacentHTML('beforeend', module.conditionExpressionHelp);
                        }
                    })
                    .catch(error => logUnableToLoadHelpError(error));

                editor.refresh();
            },
            errorFn: () => unableToLoadEditor('routingConditionEditor'),
            options: {
                readOnly: !editable ? 'nocursor' : false,
                mode: 'javascript',
                value: route.condition || '',
                lint: {
                    asi: true,
                    expr: true,
                    esversion: 6,
                },
                hintOptions: {hint: javascriptHint},
                extraKeys: {'Ctrl-Space': 'autocomplete'},
            }
        })).catch(() => unableToLoadEditor('routingConditionEditor'));
}

Sidebar.prototype.refresh = function () {
    if (this.container.style.width === '0px') {
        return;
    }

    this.clear();

    const graph = this.editorUi.editor.graph;

    if (graph.getSelectionCount() > 1) {
        return;
    }

    const containerFragment = document.createDocumentFragment();
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'ui top attached tabular stackable menu';
    containerFragment.appendChild(tabsContainer);

    if (graph.isSelectionEmpty()) {
        if (!this.activeTab?.startsWith('workflow')) {
            this.activeTab = null;
        }
        this.createWorkflowGeneralTab(tabsContainer);
        this.createWorkflowErrorHandlingTab(tabsContainer);

        if (!isEmpty(this.currentWorkflowDefinition.id)) {
            this.createWorkflowVersionsTab(tabsContainer);
        }
    } else {
        const cell = graph.getSelectionCell();
        if (graph.getModel().isEdge(cell)) {
            if (!this.activeTab?.startsWith('routing')) {
                this.activeTab = null;
            }
            this.createRoutingPropertiesTab(tabsContainer);
        } else {
            if (!this.activeTab?.startsWith('task')) {
                this.activeTab = null;
            }
            this.createTaskGeneralTab(tabsContainer);

            const outgoingEdges = graph.getModel().getEdges(cell, false, true, true);
            if (!cell.isStart() || (cell.isStart() && outgoingEdges.length > 1)) {
                this.createTaskInputParametersTab(tabsContainer);

                if (cell.isEnd()) {
                    this.createTaskWorkflowOutput(tabsContainer);
                }
                if (cell.isSimple()) {
                    this.createTaskProcessorTab(tabsContainer);
                }
                if (cell.isHttp()) {
                    this.createTaskHttpTab(tabsContainer);
                    this.createErrorHandlingTab(tabsContainer);
                }
                if (cell.isEmail()) {
                    this.createEmailTab(tabsContainer);
                }
                if (cell.isJQ()) {
                    this.createTaskJQTab(tabsContainer);
                }
                if (cell.isMap()) {
                    this.createTaskMapTab(tabsContainer);
                }
                if (cell.isWait()) {
                    this.createTaskWaitTab(tabsContainer);
                }

                this.createTaskRoutingTab(tabsContainer);
            }
        }
    }

    this.container.appendChild(containerFragment);

    const sidebar = this;
    $(this.container.querySelectorAll('.menu .item')).tab({
        autoTabActivation: sidebar.activeTab || true,
        onVisible: tabPath => {
            sidebar.activeTab = tabPath;

            const model = graph.getModel();
            const workflow = model.getRoot().getValue();
            const cell = graph.getSelectionCell();

            if (tabPath === 'workflowErrorHandling' && document.getElementById('failureWorkflowDiagram')) {
                handleWorkflowErrorHandlingTab(this.container, model, workflow, this.currentWorkflowDefinition);
            } else if (tabPath === 'taskProcessor') {
                handleTaskProcessorTab(this.container, model, cell, deepCopy(cell.getValue()));
            } else if (tabPath === 'taskTabWorkflowOutput' && document.getElementById('workflowOutputEditor')) {
                handleWorkflowOutputTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'taskTabInputParameters' && document.getElementById('inputParametersEditor')) {
                handleInputParametersTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'taskTabHttp' && document.getElementById('httpRequestEditor')) {
                handleHttpTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'taskTabEmail' && document.getElementById('emailEditor')) {
                handleEmailTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'routingProperties' && document.getElementById('routingConditionEditor')) {
                handleRoutingTab(this.container, this.editorUi.editor.editable, this, model, cell, deepCopy(cell.getValue()), graph.getTaskErrors(cell.id));
            } else if (tabPath === 'workflowVersions') {
                this.fetchVersions();
            }
        }
    });
    if (this.editorUi.editor.editable) {
        $(this.container.querySelectorAll('.ui.dropdown:not(.credentials,.failureWorkflow,.processorPage)')).dropdown({
            duration: 0
        });
        dataCopyValue(this.container);
    } else {
        this.container.querySelectorAll('input, textarea, select, button, .ui.dropdown, .ui.button').forEach(element => {
            element.disabled = true;
            element.setAttribute('disabled', '');
        });
    }
};

Sidebar.prototype.getParameterOptions = function () {
    const graph = this.editorUi.editor.graph;

    const parameterOptions = [];

    const addParameterOptions = (source, parameters) => parameters.forEach(({id, name}) => parameterOptions.push({
        id,
        title: `${source || ''}.${name || ''}`
    }));

    if (!graph.isSelectionEmpty()) {
        const selectionCell = graph.getSelectionCell();
        graph.getAscendantCells(selectionCell).forEach(vertex => {
            if (selectionCell.id !== vertex.id) {
                const task = vertex.cloneValue();
                addParameterOptions(task.name, task.parameters);
            }
        });
    }

    return parameterOptions;
}

Sidebar.prototype.parameterSearch = function (target) {
    const targetJquery = $(target);
    if (targetJquery.length === 0) {
        return;
    }

    targetJquery.search({
        source: this.getParameterOptions(),
        showNoResults: false,
        maxResults: 50,
        fullTextSearch: true,
        minCharacters: 0,
        searchFields: ['title'],
        fields: {
            id: 'id'
        },
        onSelect: function (result) {
            const fieldValue = this.querySelector('input[type="text"]');
            fieldValue.value = result.title;
            fieldValue.dispatchEvent(new CustomEvent(mxEvent.CHANGE, {detail: result.id}));
        }
    });
}

Sidebar.prototype.createWorkflowGeneralTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = model.getRoot();
    const workflow = deepCopy(cell.getValue());
    const workflowErrors = graph.getWorkflowErrors();

    const workflowNameField = this.createInputField({
        label: mxResources.get('name'),
        id: 'name',
        name: 'name',
        type: 'text',
        className: 'required',
        value: workflow.name || '',
        error: workflowErrors?.fields?.name
    });

    mxEvent.addListener(workflowNameField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.name = evt.target.value;
        model.setValue(cell, workflow);
    }));

    const workflowDescriptionField = this.createTextareaField({
        label: mxResources.get('description'),
        id: 'description',
        name: 'description',
        rows: 2,
        value: workflow.description || ''
    });
    mxEvent.addListener(workflowDescriptionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.description = evt.target.value;
        model.setValue(cell, workflow);
    }));

    const workflowTimeoutSecondsField = this.createInputField({
        label: mxResources.get('workflowTimeoutSeconds'),
        help: mxResources.get('workflowTimeoutSecondsHelp'),
        id: 'workflowTimeoutSeconds',
        name: 'workflowTimeoutSeconds',
        type: 'number',
        min: 0,
        value: workflow.timeoutSeconds || 0,
        placeHolder: 0,
        error: workflowErrors?.fields?.timeoutSeconds
    });
    mxEvent.addListener(workflowTimeoutSecondsField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.timeoutSeconds = evt.target.value;
        model.setValue(cell, workflow);
    }));

    const workflowMaxConcurrencyField = this.createInputField({
        label: mxResources.get('workflowMaxConcurrency'),
        help: mxResources.get('workflowMaxConcurrencyHelp'),
        id: 'workflowMaxConcurrency',
        name: 'workflowMaxConcurrency',
        type: 'number',
        min: 0,
        value: workflow.maxConcurrency || 0,
        placeHolder: 0,
        error: workflowErrors?.fields?.maxConcurrency
    });
    mxEvent.addListener(workflowMaxConcurrencyField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        workflow.maxConcurrency = evt.target.value;
        model.setValue(cell, workflow);
    }));

    const fieldErrors = [];
    workflowErrors?.fields?.name && fieldErrors.push(workflowErrors.fields.name);
    workflowErrors?.fields?.timeoutSeconds && fieldErrors.push(workflowErrors.fields.timeoutSeconds);
    workflowErrors?.fields?.maxConcurrency && fieldErrors.push(workflowErrors.fields.maxConcurrency);

    let objectErrors = [];
    if (Array.isArray(workflowErrors)) {
        objectErrors = workflowErrors.map(error => error.message);
    }

    const workflowProperties = this.createTab(tabsContainer, 'workflowGeneral', mxResources.get('workflowTabGeneral'), objectErrors, fieldErrors);
    workflowProperties.appendChild(workflowNameField);
    workflowProperties.appendChild(workflowDescriptionField);
    workflowProperties.appendChild(workflowTimeoutSecondsField);
    workflowProperties.appendChild(workflowMaxConcurrencyField);
};

Sidebar.prototype.createWorkflowErrorHandlingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = model.getRoot();
    const workflow = deepCopy(cell.getValue());
    const workflowErrors = graph.getWorkflowErrors();

    const fieldErrors = [];
    workflowErrors?.fields?.failureWorkflow && fieldErrors.push(workflowErrors.fields.failureWorkflow);
    workflowErrors?.fields?.failureWorkflowVersion && fieldErrors.push(workflowErrors.fields.failureWorkflowVersion);

    const workflowProperties = this.createTab(tabsContainer, 'workflowErrorHandling', mxResources.get('workflowTabErrorHandling'), [], fieldErrors);
    workflowProperties.insertAdjacentHTML('beforeend', `<p>${mxResources.get('workflowTabErrorHandlingHelp')}</p>`);

    const failureWorkflow = workflow.failureWorkflow || '';
    const failureWorkflowDropdownField = this.createDropdown({
        id: 'failureWorkflow',
        name: 'failureWorkflow',
        label: mxResources.get('failureWorkflow'),
        value: failureWorkflow,
        error: workflowErrors?.fields?.failureWorkflow,
        clearable: true,
        searchable: true,
        options: [{
            value: '',
            name: mxResources.get('failureWorkflowSelect')
        }]
    });

    const failureWorkflowVersion = workflow.failureWorkflowVersion || '';
    const failureWorkflowVersionDropdownField = this.createDropdown({
        id: 'failureWorkflowVersion',
        name: 'failureWorkflowVersion',
        label: mxResources.get('failureWorkflowVersion'),
        value: failureWorkflowVersion,
        error: workflowErrors?.fields?.failureWorkflowVersion,
        clearable: true,
        searchable: true,
        options: [{
            value: '',
            name: mxResources.get('failureWorkflowVersionSelect')
        }]
    });

    workflowProperties.appendChild(failureWorkflowDropdownField);
    workflowProperties.appendChild(failureWorkflowVersionDropdownField);

    workflowProperties.insertAdjacentHTML('beforeend', `
    <div id="failureWorkflowDiagram" class="ui segments graph-content" style="display: none"></div>
    <p>The failure workflow gets the original failed workflow's input along with the following additional items:</p>
    <ul>
      <li><code>swf:failureWorkflow</code>
        <ul>
          <li><code>swf:workflowId</code></li>
          <li><code>swf:workflowStatus</code></li>
          <li><code>swf:workflowDefinitionId</code></li>
          <li><code>swf:workflowDefinitionVersion</code></li>
          <li><code>swf:taskId</code></li>
          <li><code>swf:taskStatus</code></li>    
          <li><code>swf:cause</code></li>    
        </ul>
      </li>
    </ul>
    `);
};

Sidebar.prototype.createWorkflowVersionsTab = function (tabsContainer) {
    const workflowVersions = this.createTab(tabsContainer, 'workflowVersions', mxResources.get('workflowTabVersions'));
    workflowVersions.insertAdjacentHTML('beforeend', `
      <p>A version is an immutable snapshot of your workflow definition. When you point your application at a workflow definition version, it will continue to use that version even after you update the workflow definition.</p>
    `);
    workflowVersions.appendChild(this.createVersions());
};

Sidebar.prototype.createVersions = function () {
    const versionsContainer = document.createElement('table');
    versionsContainer.className = 'ui striped compact table';
    versionsContainer.insertAdjacentHTML('beforeend', `<thead><th class="collapsing right aligned">${mxResources.get('version')}</th><th>${mxResources.get('enabled')}</th><th>${mxResources.get('updatedOn')}</th><th class="collapsing"></th></tr></thead>`);

    const tableBody = document.createElement('tbody');
    tableBody.insertAdjacentHTML('beforeend', `<tr><td colspan="3"><div class="ui active centered inline loader"></div></td></tr>`);
    versionsContainer.appendChild(tableBody);

    return versionsContainer;
}

Sidebar.prototype.fetchVersions = function () {
    const workflowDefinitionId = this.currentWorkflowDefinition.id;
    if (workflowDefinitionId && workflowDefinitionId.length > 0) {
        api(`/api/workflow-definitions/${workflowDefinitionId}/versions`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        }).then(workflowDefinitions => {
            const versionsBody = document.createElement('tbody');

            if (isEmpty(workflowDefinitions)) {
                versionsBody.insertAdjacentHTML('beforeend', `
                  <tr>
                      <td colspan="3">${mxResources.get('noVersions')}</td>
                  </tr>
              `);
            } else {
                workflowDefinitions.forEach((workflowDefinition, index) => {
                    versionsBody.insertAdjacentHTML('beforeend', `
                                <tr>
                                    <td class="collapsing right aligned"><a href="/workflow-definitions/${workflowDefinition.id}?version=${workflowDefinition.version}">${workflowDefinition.version}</a></td>
                                    <td>
                                      <div class="ui fitted toggle checkbox">
                                      <input type="checkbox" name="disabledVersions" value="${workflowDefinition.version}"${workflowDefinition.disabled === false ? ' checked="checked"' : ''}>
                                      <label></label>
                                      </div>
                                    </td>
                                    <td><relative-time datetime="${workflowDefinition.createdOn}">${new Intl.DateTimeFormat(
                        navigator.language || 'default', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                        }).format(new Date(workflowDefinition.createdOn))}</relative-time></td>
                                </tr>
                            `);
                });
            }

            document.querySelector('#workflowVersionsContainer tbody').replaceWith(versionsBody);
            document.querySelectorAll('#workflowVersionsContainer tbody input[name="disabledVersions"]').forEach(el => {
                el.addEventListener('click', e => {
                    const operation = e.target.checked ? 'enable' : 'disable';
                    api(`/api/workflow-definitions/${workflowDefinitionId}/${operation}?version=${e.target.value}`, {
                        method: 'PUT'
                    }).catch(() => {
                        e.target.checked = !e.target.checked;
                    });
                })
            });
        }).catch(() => {
            const tableData = document.createElement('td');
            tableData.colSpan = 4;
            tableData.className = 'ui text error';
            tableData.textContent = `${mxResources.get('errorFetchingVersions')} `;

            const refreshLink = document.createElement('a');
            refreshLink.href = '#'
            refreshLink.textContent = mxResources.get('pleaseTryAgain');
            refreshLink.addEventListener('click', event => {
                event.preventDefault();
                this.createVersions();
                this.fetchVersions();
            });

            tableData.appendChild(refreshLink);
            tableData.appendChild(document.createTextNode('.'));

            const tableRow = document.createElement('tr');
            tableRow.appendChild(tableData);

            const retryBody = document.createElement('tbody');
            retryBody.appendChild(tableRow);

            document.querySelector('#workflowVersionsContainer tbody').replaceWith(retryBody);
        });
    } else {
        const noVersionsBody = document.createElement('tbody');
        noVersionsBody.insertAdjacentHTML('beforeend', `<tr><td colspan="4">${mxResources.get('noVersions')}</td></tr>`);
        document.querySelector('#workflowVersionsContainer tbody').replaceWith(noVersionsBody);
    }
}

Sidebar.prototype.createRoutingPropertiesTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = graph.getSelectionCell();
    const route = deepCopy(cell.getValue());
    const routeErrors = graph.getRouteErrors(cell.id);

    const routingNameField = this.createInputField({
        label: mxResources.get('name'),
        id: 'name',
        name: 'name',
        type: 'text',
        value: route.name || ''
    });
    mxEvent.addListener(routingNameField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        route.name = evt.target.value;
        model.setValue(cell, route);
    }));

    const fieldErrors = [];
    routeErrors?.fields?.routingType && fieldErrors.push(routeErrors.fields.routingType);
    routeErrors?.fields?.condition && fieldErrors.push(routeErrors.fields.condition);

    const objectErrors = [];
    routeErrors?.message && objectErrors.push(routeErrors?.message);

    const routingProperties = this.createTab(tabsContainer, 'routingProperties', mxResources.get('routingTabProperties'), objectErrors, fieldErrors);
    routingProperties.appendChild(routingNameField);

    const outgoingEdges = graph.getModel().getEdges(cell.source, false, true, true);
    if ((!cell.source.isStart() || (cell.source.isStart() && outgoingEdges.length > 1)) && !cell.source.isSplit() && !cell.target.isJoin()) {
        const hasUnconditional = !cell.source.isSplit() && cell.source.edges.some(edge => edge.source.id === cell.source.id && edge.id !== cell.id && !edge.isConditional());

        const routingType = route.routingType;
        const routingTypeFields = this.createRadioFields({
            label: mxResources.get('conditionalRouting'),
            name: 'routingType',
            value: routingType,
            error: routeErrors?.fields?.routingType,
            className: 'required',
            options: [{
                label: mxResources.get('alwaysTrue'),
                className: 'inline',
                help: hasUnconditional ? mxResources.get('alwaysTrueDisabled') : null,
                value: 'UnConditional',
                disabled: hasUnconditional,
                onChange: mxUtils.bind(this, function (evt) {
                    route.routingType = 'UnConditional';
                    route.condition = null;
                    model.setValue(cell, route);
                    graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
                })
            }, {
                label: mxResources.get('condition'),
                className: 'inline',
                help: mxResources.get('conditionHelp'),
                value: 'Conditional',
                disabled: outgoingEdges.length <= 1,
                onChange: mxUtils.bind(this, function (evt) {
                    route.routingType = 'Conditional';
                    model.setValue(cell, route);
                    graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
                })
            }]
        });

        routingProperties.appendChild(routingTypeFields);

        if (routingType === 'Conditional') {
            const routingConditionEditor = document.createElement('div');
            routingConditionEditor.id = 'routingConditionEditor';
            routingConditionEditor.className = 'ui active centered inline loader';
            routingProperties.appendChild(routingConditionEditor);
        }
    }
};

Sidebar.prototype.createTaskGeneralTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const cell = graph.getSelectionCell();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.name && fieldErrors.push(taskErrors.fields.name);
    taskErrors?.fields?.terminationStatus && fieldErrors.push(taskErrors.fields.terminationStatus);

    const objectErrors = [];
    taskErrors?.message && objectErrors.push(taskErrors?.message);
    taskErrors?.fields?.branches && objectErrors.push(taskErrors.fields.branches);
    taskErrors?.fields?.choices && objectErrors.push(taskErrors.fields.choices);

    const taskGeneral = this.createTab(tabsContainer, 'taskGeneral', mxResources.get('taskTabGeneral'), objectErrors, fieldErrors);

    const taskNameField = this.createInputField({
        label: mxResources.get('name'),
        id: 'name',
        name: 'name',
        type: 'text',
        className: 'required',
        value: task.name,
        error: taskErrors?.fields?.name
    });
    mxEvent.addListener(taskNameField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.name = evt.target.value;
        model.setValue(cell, task);
    }));
    taskGeneral.appendChild(taskNameField);

    const outgoingEdges = graph.getModel().getEdges(cell, false, true, true);
    if (!cell.isStart() || (cell.isStart() && outgoingEdges.length > 1)) {
        const taskDescriptionField = this.createTextareaField({
            label: mxResources.get('description'),
            id: 'description',
            name: 'description',
            rows: 2,
            value: task.description || ''
        });
        mxEvent.addListener(taskDescriptionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
            task.description = evt.target.value;
            model.setValue(cell, task);
        }));
        taskGeneral.appendChild(taskDescriptionField);
    }

    if (cell.isEnd()) {
        const terminationStatus = task.terminationStatus;

        const terminationStatusFields = this.createRadioFields({
            label: mxResources.get('terminationStatus'),
            name: 'terminationStatus',
            className: 'required',
            value: terminationStatus,
            options: [{
                label: mxResources.get('terminationStatusCompleted'),
                className: 'inline',
                help: mxResources.get('terminationStatusCompletedHelp'),
                value: 'Completed',
                onChange: mxUtils.bind(this, function (evt) {
                    task.terminationStatus = 'Completed';
                    model.setValue(cell, task);
                })
            }, {
                label: mxResources.get('terminationStatusFailed'),
                className: 'inline',
                help: mxResources.get('terminationStatusFailedHelp'),
                value: 'Failed',
                onChange: mxUtils.bind(this, function (evt) {
                    task.terminationStatus = 'Failed';
                    model.setValue(cell, task);
                })
            }]
        });

        taskGeneral.appendChild(terminationStatusFields);
    }
};

Sidebar.prototype.createTaskInputParametersTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.inputParameters && fieldErrors.push(taskErrors.fields.inputParameters);

    const taskTabInputParameters = this.createTab(tabsContainer, 'taskTabInputParameters', mxResources.get('taskTabInputParameters'), [], fieldErrors);
    const inputParametersEditor = document.createElement('div');
    inputParametersEditor.id = 'inputParametersEditor';
    inputParametersEditor.className = 'ui active centered inline loader';

    taskTabInputParameters.appendChild(inputParametersEditor);
};

Sidebar.prototype.createTaskHttpTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = prepareHttpTaskErrors(taskErrors);

    const taskTabHttp = this.createTab(tabsContainer, 'taskTabHttp', mxResources.get('taskTabHttp'), [], fieldErrors);

    const defaultOption = {
        value: '',
        name: mxResources.get('credentialsSelect')
    };

    const credentialsDropdownField = this.createDropdown({
        label: mxResources.get('credentials'),
        id: 'credentials',
        name: 'credentials',
        error: taskErrors?.fields?.credentials,
        clearable: true,
        searchable: true,
        options: [defaultOption]
    });

    taskTabHttp.appendChild(credentialsDropdownField);

    const httpRequestEditor = document.createElement('div');
    httpRequestEditor.id = 'httpRequestEditor';
    httpRequestEditor.className = 'ui active centered inline loader';

    taskTabHttp.appendChild(httpRequestEditor);

    const defaultConnectionTimeoutSeconds = 5;
    const defaultReadTimeoutSeconds = 10;
    const connectionTimeoutSecondsField = this.createInputField({
        label: mxResources.get('connectionTimeoutSeconds'),
        id: 'connectionTimeoutSeconds',
        name: 'connectionTimeoutSeconds',
        type: 'number',
        min: 1,
        value: task.connectionTimeoutSeconds || defaultConnectionTimeoutSeconds,
        placeHolder: defaultConnectionTimeoutSeconds,
        error: taskErrors?.fields?.connectionTimeoutSeconds
    });
    mxEvent.addListener(connectionTimeoutSecondsField, mxEvent.CHANGE,
        mxUtils.bind(this, function (evt) {
            task.connectionTimeoutSeconds = evt.target.value;
            model.setValue(cell, task);
        }));
    taskTabHttp.appendChild(connectionTimeoutSecondsField);

    const readTimeoutSecondsField = this.createInputField({
        label: mxResources.get('readTimeoutSeconds'),
        id: 'readTimeoutSeconds',
        name: 'readTimeoutSeconds',
        type: 'number',
        min: 1,
        value: task.readTimeoutSeconds || defaultReadTimeoutSeconds,
        placeHolder: defaultReadTimeoutSeconds,
        error: taskErrors?.fields?.readTimeoutSeconds
    });
    mxEvent.addListener(readTimeoutSecondsField, mxEvent.CHANGE,
        mxUtils.bind(this, function (evt) {
            task.readTimeoutSeconds = evt.target.value;
            model.setValue(cell, task);
        }));
    taskTabHttp.appendChild(readTimeoutSecondsField);
};

Sidebar.prototype.createEmailTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();

    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = prepareEmailTaskErrors(taskErrors);

    const taskTabEmail = this.createTab(tabsContainer, 'taskTabEmail', mxResources.get('taskTabEmail'), [], fieldErrors);

    const emailEditor = document.createElement('div');
    emailEditor.id = 'emailEditor';
    emailEditor.className = 'ui active centered inline loader';

    taskTabEmail.appendChild(emailEditor);
};

Sidebar.prototype.createTaskJQTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.filter && fieldErrors.push(taskErrors.fields.filter);

    const taskTabJQ = this.createTab(tabsContainer, 'taskTabJQ', mxResources.get('taskTabJQ'), [], fieldErrors);
    const taskFilterField = this.createTextareaField({
        label: mxResources.get('jqFilter'),
        id: 'filter',
        name: 'filter',
        rows: 5,
        className: 'required',
        value: task.filter,
        error: taskErrors?.fields?.filter
    });

    mxEvent.addListener(taskFilterField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.filter = evt.target.value;
        model.setValue(cell, task);
    }));

    taskTabJQ.insertAdjacentHTML('beforeend', `
    <p>Use JQ filter expression to process input parameters JSON data. See <a target="_blank" rel="noopener noreferrer" href="https://stedolan.github.io/jq/manual/v1.6/">JQ Manual</a> to learn more.</p>
    `);
    taskTabJQ.appendChild(taskFilterField);

    const testJQFilterButton = document.createElement('button');
    testJQFilterButton.id = 'testJQFilter';
    testJQFilterButton.className = 'ui mini compact button';
    testJQFilterButton.textContent = mxResources.get('testJQFilter')

    const testJQFilterField = document.createElement('div');
    testJQFilterField.id = 'testJQFilterField'
    testJQFilterField.className = 'field';
    testJQFilterField.appendChild(testJQFilterButton);

    taskTabJQ.appendChild(testJQFilterField);

    mxEvent.addListener(testJQFilterField, mxEvent.CLICK, mxUtils.bind(this, function (e) {
        if (isEmpty(task.filter)) {
            document.getElementById('testJQFilterResponse')?.remove();
            $(e.target).api('destroy');
            e.preventDefault();
            return;
        }

        $(e.target).api({
            url: '/api/jq-test',
            method: 'POST',
            data: stringifyJSON({
                input: task.inputParameters,
                jqFilter: task.filter
            }),
            beforeXHR: function (xhr) {
                xhr.setRequestHeader('Content-Type', 'application/json');
                return xhr;
            },
            onComplete: function (response, element, xhr) {
                document.getElementById('testJQFilterResponse')?.remove();

                const testJQFilterResponseField = document.createElement('div');
                testJQFilterResponseField.id = 'testJQFilterResponse';
                testJQFilterResponseField.className = 'field';
                testJQFilterResponseField.insertAdjacentHTML('beforeend', `<label>${mxResources.get('httpResponse', [xhr.status, xhr.statusText])}</label>`);

                import(/* webpackChunkName: "code-editor" */ '../Common/codeEditor').then(
                    module => module.codeEditor({
                        callbackFn: editor => {
                            if (!isEmpty(response)) {
                                editor.setValue(stringifyJSON(response, '', 2));
                            } else {
                                editor.setValue('');
                            }

                            testJQFilterResponseField.appendChild(editor.getWrapperElement());

                            document.getElementById('testJQFilterField').insertAdjacentElement('afterend', testJQFilterResponseField);

                            editor.refresh();
                        },
                        errorFn: () => {
                            testJQFilterResponseField.insertAdjacentHTML('beforeend', `<pre class="ui container scrolling very short"><code>${stringifyJSON(response, '', 2)}</code></pre>`);
                            document.getElementById('testJQFilterResponse').insertAdjacentElement('afterend', testJQFilterResponseField);
                        },
                        options: {
                            mode: 'application/json',
                            readOnly: true,
                            foldGutter: true,
                            lineWrapping: true,
                            gutters: ['CodeMirror-lint-markers', 'CodeMirror-foldgutter'],
                        },
                    }),
                );
            }
        }).api('query');
    }));
};

Sidebar.prototype.createTaskMapTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const objectErrors = [];
    taskErrors?.fields?.itemProcessor?.tasks && objectErrors.push(taskErrors.fields.itemProcessor.tasks);

    const fieldErrors = [];
    taskErrors?.fields?.itemsExpression && fieldErrors.push(taskErrors.fields.itemsExpression);
    taskErrors?.fields?.maxConcurrency && fieldErrors.push(taskErrors.fields.maxConcurrency);
    taskErrors?.fields?.maxConcurrencyExpression && fieldErrors.push(taskErrors.fields.maxConcurrencyExpression);

    const taskTabMap = this.createTab(tabsContainer, 'taskTabMap', mxResources.get('taskTabMap'), objectErrors, fieldErrors);
    const taskMapItemsExpressionField = this.createInputField({
        label: mxResources.get('taskMapItemsExpression'),
        help: mxResources.get('taskMapItemsExpressionHelp'),
        id: 'itemsExpression',
        name: 'itemsExpression',
        type: 'text',
        className: 'required',
        placeHolder: '${ }',
        value: task.itemsExpression,
        error: taskErrors?.fields?.itemsExpression
    });

    mxEvent.addListener(taskMapItemsExpressionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.itemsExpression = evt.target.value;
        model.setValue(cell, task);
    }));

    const taskMapMaxConcurrencyField = this.createInputField({
        label: mxResources.get('taskMaxConcurrency'),
        id: 'taskMaxConcurrency',
        name: 'taskMaxConcurrency',
        type: 'number',
        min: 0,
        value: task.maxConcurrency,
        placeHolder: 0,
        error: taskErrors?.fields?.maxConcurrency
    });
    mxEvent.addListener(taskMapMaxConcurrencyField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.maxConcurrency = evt.target.value;
        model.setValue(cell, task);
    }));

    const taskMapMaxConcurrencyExpressionField = this.createInputField({
        label: mxResources.get('taskMaxConcurrencyExpression'),
        id: 'taskMaxConcurrencyExpression',
        name: 'taskMaxConcurrencyExpression',
        type: 'text',
        placeHolder: '${ }',
        value: task.maxConcurrencyExpression,
        error: taskErrors?.fields?.maxConcurrencyExpression
    });

    import(/* webpackChunkName: "jsonExpressionHelp" */ "./help/jsonExpressionHelp")
        .then(module => taskMapMaxConcurrencyExpressionField.insertAdjacentHTML('beforeend', module.jsonExpressionHelp))
        .catch(error => LOGGER.error("Unable to load help context", error));

    mxEvent.addListener(taskMapMaxConcurrencyExpressionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.maxConcurrencyExpression = evt.target.value;
        model.setValue(cell, task);
    }));

    taskTabMap.appendChild(taskMapItemsExpressionField);
    taskTabMap.insertAdjacentHTML('beforeend', `<h4 class="ui dividing header">${mxResources.get('taskConcurrency')}</h4>`);
    taskTabMap.appendChild(taskMapMaxConcurrencyField);
    taskTabMap.insertAdjacentHTML('beforeend', '<div class="ui horizontal divider">Or</div>');
    taskTabMap.appendChild(taskMapMaxConcurrencyExpressionField);
};

const wrap = (el, wrapper) => {
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
}

Sidebar.prototype.createTaskWaitTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.seconds && fieldErrors.push(taskErrors.fields.seconds);
    taskErrors?.fields?.secondsExpression && fieldErrors.push(taskErrors.fields.secondsExpression);
    taskErrors?.fields?.timestamp && fieldErrors.push(taskErrors.fields.timestamp);
    taskErrors?.fields?.timestampExpression && fieldErrors.push(taskErrors.fields.timestampExpression);

    const min = 30;
    const taskWaitSecondsField = this.createInputField({
        label: mxResources.get('waitSeconds'),
        id: 'seconds',
        name: 'seconds',
        type: 'number',
        min,
        className: 'required',
        value: task.seconds,
        error: taskErrors?.fields?.seconds
    });
    mxEvent.addListener(taskWaitSecondsField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.seconds = evt.target.value;
        model.setValue(cell, task);
    }));

    const taskWaitSecondsExpressionField = this.createInputField({
        label: mxResources.get('waitSecondsExpression'),
        id: 'secondsExpression',
        name: 'secondsExpression',
        type: 'text',
        className: 'required',
        placeHolder: '${ }',
        value: task.secondsExpression,
        error: taskErrors?.fields?.secondsExpression
    });

    import(/* webpackChunkName: "jsonExpressionHelp" */ "./help/jsonExpressionHelp")
        .then(module => taskWaitSecondsExpressionField.insertAdjacentHTML('beforeend', module.jsonExpressionHelp))
        .catch(error => LOGGER.error("Unable to load help context", error));

    mxEvent.addListener(taskWaitSecondsExpressionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.secondsExpression = evt.target.value;
        model.setValue(cell, task);
    }));

    const taskWaitTimestampField = this.createInputField({
        label: mxResources.get('waitTimestamp'),
        id: 'timestamp',
        name: 'timestamp',
        type: 'text',
        className: 'required',
        value: task.timestamp,
        error: taskErrors?.fields?.timestamp
    });

    const taskWaitTimestampExpressionField = this.createInputField({
        label: mxResources.get('waitTimestampExpression'),
        id: 'waitTimestampExpression',
        name: 'waitTimestampExpression',
        type: 'text',
        className: 'required',
        placeHolder: '${ }',
        value: task.timestampExpression,
        error: taskErrors?.fields?.timestampExpression
    });

    import(/* webpackChunkName: "jsonExpressionHelp" */ "./help/jsonExpressionHelp")
        .then(module => taskWaitTimestampExpressionField.insertAdjacentHTML('beforeend', module.jsonExpressionHelp))
        .catch(() => taskWaitTimestampExpressionField.insertAdjacentHTML('beforeend', '<div class="ui message error visible">Unable to load help context.</div>'));

    mxEvent.addListener(taskWaitTimestampExpressionField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        task.timestampExpression = evt.target.value;
        model.setValue(cell, task);
    }));

    const tabWait = this.createTab(tabsContainer, 'taskWait', mxResources.get('taskTabWait'), [], fieldErrors);
    tabWait.appendChild(taskWaitSecondsField);
    tabWait.insertAdjacentHTML('beforeend', '<div class="ui horizontal divider">Or</div>');
    tabWait.appendChild(taskWaitSecondsExpressionField);
    tabWait.insertAdjacentHTML('beforeend', '<div class="ui horizontal divider">Or</div>');
    tabWait.appendChild(taskWaitTimestampField);
    tabWait.insertAdjacentHTML('beforeend', '<div class="ui horizontal divider">Or</div>');
    tabWait.appendChild(taskWaitTimestampExpressionField);

    const calendarWrapper = document.createElement('div');
    calendarWrapper.className = 'ui input left icon';
    calendarWrapper.insertAdjacentHTML('beforeend', '<i class="calendar icon"></i>');

    wrap(taskWaitTimestampField.querySelector('input'), calendarWrapper);

    mxEvent.addListener(taskWaitTimestampField, mxEvent.CHANGE, mxUtils.bind(this, function (evt) {
        const selectedDate = $(taskWaitTimestampField).calendar('get date');
        task.timestamp = selectedDate?.toISOString();
        model.setValue(cell, task);
    }));

    $(taskWaitTimestampField).calendar({
        type: 'datetime',
        minDate: new Date(),
        minTimeGap: min
    })
};

Sidebar.prototype.createTaskProcessorTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.processor && fieldErrors.push(taskErrors.fields.processor);

    const tabProcessor = this.createTab(tabsContainer, 'taskProcessor', mxResources.get('taskTabProcessor'), [], fieldErrors);
    tabProcessor.insertAdjacentHTML('beforeend', `<p>${mxResources.get('taskTabProcessorHelp')}</p>`);

    const defaultOption = {
        value: '',
        name: mxResources.get('processorPage')
    };

    const processor = task.processor || '';
    const pageDropdownField = this.createDropdown({
        id: 'processorPage',
        name: 'processorPage',
        value: processor,
        error: taskErrors?.fields?.processor,
        clearable: true,
        searchable: true,
        options: [defaultOption]
    });

    tabProcessor.appendChild(pageDropdownField);

    if (!isEmpty(processor)) {
        api(`/pages/${processor}?embed=true`, {
            method: 'GET',
            headers: {
                'Accept': 'text/html'
            }
        })
            .then(pageContent => {
                tabProcessor.insertAdjacentHTML('beforeend', pageContent);
                $(tabProcessor.querySelectorAll('.markdown-body .ui.dropdown')).dropdown({duration: 0});
            })
            .catch(error => LOGGER.error("Unable to load task processor page", error));
    }
};

Sidebar.prototype.createErrorHandlingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.intervalSeconds && fieldErrors.push(taskErrors.fields.intervalSeconds);
    taskErrors?.fields?.maxAttempts && fieldErrors.push(taskErrors.fields.maxAttempts);
    taskErrors?.fields?.backoffRate && fieldErrors.push(taskErrors.fields.backoffRate);

    const taskErrorHandling = this.createTab(tabsContainer, 'taskErrorHandling', mxResources.get('taskErrorHandling'), [], fieldErrors);
    taskErrorHandling.insertAdjacentHTML('beforeend', `
      <p>Task can encounter runtime errors. Errors can arise because of workflow definition issues, task failures or because of transient issues, such as network partition events.</p>
      `);

    const hasRetry = task.retryIntervalSeconds || task.retryMaxAttempts || task.retryBackoffRate;
    const defaultIntervalSeconds = '1';
    const defaultMaxAttempts = '3';
    const defaultBackoffRate = '2.0';

    const taskErrorHandlingTypeFields = this.createRadioFields({
        label: mxResources.get('taskErrorHandlingType'),
        name: 'taskErrorHandlingType',
        value: hasRetry ? 'yes' : 'no',
        className: 'required',
        options: [{
            label: mxResources.get('taskErrorHandlingNo'),
            className: 'inline',
            value: 'no',
            onChange: mxUtils.bind(this, function (evt) {
                task.retryIntervalSeconds = null;
                task.retryMaxAttempts = null;
                task.retryBackoffRate = null;
                model.setValue(cell, task);
            })
        }, {
            label: mxResources.get('taskErrorHandlingYes'),
            className: 'inline',
            value: 'yes',
            onChange: mxUtils.bind(this, function (evt) {
                task.retryIntervalSeconds = task.retryIntervalSeconds || defaultIntervalSeconds;
                task.retryMaxAttempts = task.retryMaxAttempts || defaultMaxAttempts;
                task.retryBackoffRate = task.retryBackoffRate || defaultBackoffRate;
                model.setValue(cell, task);
            })
        }]
    });

    taskErrorHandling.appendChild(taskErrorHandlingTypeFields)

    if (hasRetry) {
        const taskRetryIntervalSecondsField = this.createInputField({
            label: mxResources.get('retryIntervalSeconds'),
            id: 'retryIntervalSeconds',
            name: 'retryIntervalSeconds',
            type: 'number',
            min: 1,
            value: task.retryIntervalSeconds,
            placeHolder: defaultIntervalSeconds,
            help: mxResources.get('retryIntervalSecondsHelp'),
            error: taskErrors?.fields?.intervalSeconds
        });
        mxEvent.addListener(taskRetryIntervalSecondsField, mxEvent.CHANGE,
            mxUtils.bind(this, function (evt) {
                task.retryIntervalSeconds = evt.target.value;
                model.setValue(cell, task);
            }));
        taskErrorHandling.appendChild(taskRetryIntervalSecondsField);

        const taskRetryMaxAttemptsField = this.createInputField({
            label: mxResources.get('retryMaxAttempts'),
            id: 'retryMaxAttempts',
            name: 'retryMaxAttempts',
            type: 'number',
            min: 0,
            value: task.retryMaxAttempts,
            placeHolder: defaultMaxAttempts,
            help: mxResources.get('retryMaxAttemptsHelp'),
            error: taskErrors?.fields?.maxAttempts
        });
        mxEvent.addListener(taskRetryMaxAttemptsField, mxEvent.CHANGE,
            mxUtils.bind(this, function (evt) {
                task.retryMaxAttempts = evt.target.value;
                model.setValue(cell, task);
            }));
        taskErrorHandling.appendChild(taskRetryMaxAttemptsField);

        const taskRetryBackoffRateField = this.createInputField({
            label: mxResources.get('retryBackoffRate'),
            id: 'retryBackoffRate',
            name: 'retryBackoffRate',
            type: 'number',
            min: '1.0',
            step: '0.1',
            placeHolder: defaultBackoffRate,
            value: task.retryBackoffRate,
            help: mxResources.get('retryBackoffRateHelp'),
            error: taskErrors?.fields?.backoffRate
        });
        mxEvent.addListener(taskRetryBackoffRateField, mxEvent.CHANGE,
            mxUtils.bind(this, function (evt) {
                task.retryBackoffRate = evt.target.value;
                model.setValue(cell, task);
            }));
        taskErrorHandling.appendChild(taskRetryBackoffRateField);

        taskErrorHandling.insertAdjacentHTML('beforeend', `
      <p>An example of a retry which will make 2 retry attempts after waits of 3 and 4.5 seconds:</p>
      <ul>
        <li><strong>${mxResources.get('retryIntervalSeconds')}:</strong> 3</li>
        <li><strong>${mxResources.get('retryMaxAttempts')}:</strong> 2</li>
        <li><strong>${mxResources.get('retryBackoffRate')}:</strong> 1.5</li>
      </ul>
      `);
    }
};

Sidebar.prototype.createTaskRoutingTab = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.takeRoutesOf && fieldErrors.push(taskErrors.fields.takeRoutesOf);

    const taskRouting = this.createTab(tabsContainer, 'taskRouting', mxResources.get('taskTabRouting'), [], fieldErrors);

    if (!cell.isStart()) {
        taskRouting.appendChild(this.createTaskIncomingRouting(cell));
    }

    if (!cell.isEnd()) {
        taskRouting.appendChild(this.createTaskOutgoingRouting(cell));
    }
};

Sidebar.prototype.createTaskIncomingRouting = function (cell) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());

    const routingContainer = document.createElement('div');
    routingContainer.className = 'field';

    routingContainer.insertAdjacentHTML('beforeend', `<h4 class="ui dividing header">${mxResources.get('taskIncomingRouting')}</h4>`);

    const joinType = task.joinType;
    const collectorTaskFields = this.createCheckboxes({
        name: 'joinType',
        value: joinType,
        type: 'checkbox',
        options: [{
            label: mxResources.get('collectorTask'),
            help: mxResources.get('collectorTaskHelp'),
            className: 'inline',
            value: 'And',
            onChange: mxUtils.bind(this, function (evt) {
                task.joinType = evt.target.checked ? 'And' : 'Or';
                model.setValue(cell, task);
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }]
    });

    routingContainer.appendChild(collectorTaskFields);

    return routingContainer
}

Sidebar.prototype.createTaskOutgoingRouting = function (cell) {
    const graph = this.editorUi.editor.graph;
    const model = graph.getModel();
    const task = deepCopy(cell.getValue());
    const taskErrors = graph.getTaskErrors(cell.id);

    const routingContainer = document.createElement('div');
    routingContainer.className = 'field';

    routingContainer.insertAdjacentHTML('beforeend', `<h4 class="ui dividing header">${mxResources.get('taskOutgoingRouting')}</h4>`);

    const splitType = task.splitType;
    const takeRoutesOfFields = this.createRadioFields({
        label: mxResources.get('takeRoutesOf'),
        name: 'takeRoutesOf',
        value: splitType,
        error: taskErrors?.fields?.takeRoutesOf,
        className: 'required',
        options: [{
            label: mxResources.get('firstTrueConditions'),
            className: 'inline',
            help: mxResources.get('firstTrueConditionsHelp'),
            value: 'Or',
            onChange: mxUtils.bind(this, function (evt) {
                task.splitType = 'Or';
                model.setValue(cell, task);
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }, {
            label: mxResources.get('allTrueConditions'),
            className: 'inline',
            help: mxResources.get('allTrueConditionsHelp'),
            value: 'And',
            onChange: mxUtils.bind(this, function (evt) {
                task.splitType = 'And';
                model.setValue(cell, task);
                graph.fireEvent(new mxEventObject(UPDATE_CELL_VALUE, 'cell', cell));
            })
        }]
    });

    routingContainer.appendChild(takeRoutesOfFields);

    const outgoingEdges = model.getEdges(cell, false, true, true);
    outgoingEdges.sort((a, b) => a.isConditional() === b.isConditional() ? 0 : a.isConditional() ? -1 : 1);
    if (outgoingEdges.length > 0) {
        const routingList = document.createElement(`${cell.isSplit() ? 'ul' : 'ol'}`);
        routingList.className = 'ui suffixed list';
        outgoingEdges.forEach(edge => {
            const {value: route, target: {value: targetTask}} = edge;
            const routing = document.createElement('li');
            routing.textContent = mxResources.get('taskOutgoingRoute', [route.name || '<blank>', targetTask.name]);
            routingList.appendChild(routing);
        });

        const routingInfo = document.createElement('div');
        routingInfo.className = 'ui message';
        routingInfo.appendChild(routingList);
        routingContainer.appendChild(routingInfo);
    }

    return routingContainer;
};

Sidebar.prototype.createTaskWorkflowOutput = function (tabsContainer) {
    const graph = this.editorUi.editor.graph;
    const cell = graph.getSelectionCell();
    const taskErrors = graph.getTaskErrors(cell.id);

    const fieldErrors = [];
    taskErrors?.fields?.workflowOutput && fieldErrors.push(taskErrors.fields.workflowOutput);

    const taskTabWorkflowOutput = this.createTab(tabsContainer, 'taskTabWorkflowOutput', mxResources.get('taskTabWorkflowOutput'), [], taskErrors);

    taskTabWorkflowOutput.insertAdjacentHTML('beforeend', `<p>${mxResources.get('workflowOutputHelp')}</p>`);

    const workflowOutputEditor = document.createElement('div');
    workflowOutputEditor.id = 'workflowOutputEditor';
    workflowOutputEditor.className = 'ui active centered inline loader';

    taskTabWorkflowOutput.appendChild(workflowOutputEditor);
};

Sidebar.prototype.createTab = function (tabsContainer, id, name, objectErrors = [], fieldErrors = []) {
    const tab = document.createElement('a');
    tab.className = 'item';
    tab.setAttribute('data-tab', id);
    tab.appendChild(document.createTextNode(name));
    tab.id = id;

    const errorCount = (objectErrors?.length || 0) + (fieldErrors?.length || 0);
    if (errorCount > 0) {
        tab.insertAdjacentHTML('beforeend', `<a class="ui mini red circular label">${errorCount}</a>`);
    }

    tabsContainer.appendChild(tab);

    const container = document.createElement('div');
    container.setAttribute('data-tab', id);
    container.className = 'ui bottom attached tab segment form';
    container.id = `${id}Container`;

    if (!isEmpty(objectErrors)) {
        container.insertAdjacentHTML('beforeend', `<div class="ui visible message error"><ul class="list">${objectErrors.map(error => `<li>${error}</li>`).join('\n')}</ul></div>`);
    }

    tabsContainer.parentNode.appendChild(container);

    return container;
};

Sidebar.prototype.createFields = function (id, fieldElements = []) {
    const fields = document.createElement('div');
    fields.className = 'fields';
    fields.id = id;
    fieldElements.forEach(fieldElement => fields.appendChild(fieldElement));
    return fields;
};

Sidebar.prototype.createField = function ({label, id, className = '', fieldElement, help, error} = {}) {
    const fieldContainer = document.createElement('div');
    fieldContainer.className = (className ? className + ' ' : '') + 'field';

    if (id) {
        fieldContainer.id = id;
    }

    if (label) {
        if (label instanceof HTMLDivElement) {
            fieldContainer.className += ' right labeled';
            fieldContainer.appendChild(label);
        } else {
            const labelElement = document.createElement('label');
            labelElement.setAttribute('for', fieldElement.id);
            labelElement.textContent = label;
            fieldContainer.appendChild(labelElement);
        }
    }

    fieldContainer.appendChild(fieldElement);

    if (!isEmpty(error)) {
        const errorField = document.createElement('div');

        if (Array.isArray(error) && error.length > 1) {
            errorField.className = `${fieldErrorClassName} list`;
            errorField.insertAdjacentHTML('beforeend', `<ul class="list">${error.map(message => `<li>${message}</li>`).join('\n')}</ul>`);
        } else {
            errorField.className = fieldErrorClassName;
            errorField.textContent = Array.isArray(error) && error.length === 1 ? error[0] : error;
        }
        fieldContainer.appendChild(errorField);
    }

    if (help) {
        fieldContainer.insertAdjacentHTML('beforeend', `<div class="ui top pointing basic large label">${help}</div>`);
    }

    return fieldContainer;
};

Sidebar.prototype.createInputField = function ({
                                                   label,
                                                   id,
                                                   name,
                                                   className = '',
                                                   type,
                                                   step,
                                                   min,
                                                   value = '',
                                                   placeHolder,
                                                   readOnly,
                                                   disabled,
                                                   help,
                                                   error
                                               } = {}) {
    let inputElement = document.createElement('input');
    if (id) {
        inputElement.id = id;
    }
    inputElement.name = name;
    inputElement.type = type;
    inputElement.value = value;

    if (step) {
        inputElement.step = step;
    }

    if (placeHolder) {
        inputElement.placeholder = placeHolder;
    }

    if (readOnly) {
        inputElement.readOnly = true;
        inputElement.setAttribute('readOnly', '');
    }

    if (typeof min !== 'undefined') {
        inputElement.min = min;
    }

    if (disabled) {
        inputElement.disabled = true;
        inputElement.setAttribute('disabled', '');
    }

    return this.createField({label, className, fieldElement: inputElement, help, error});
};

Sidebar.prototype.createTextareaField = function ({
                                                      label,
                                                      id,
                                                      name,
                                                      rows,
                                                      className = '',
                                                      value = '',
                                                      placeHolder,
                                                      error
                                                  } = {}) {
    const textAreaElement = document.createElement('textarea');
    if (id) {
        textAreaElement.id = id;
    }
    if (rows) {
        textAreaElement.rows = rows;
    }
    textAreaElement.name = name;
    textAreaElement.textContent = value;

    if (placeHolder) {
        textAreaElement.placeholder = placeHolder;
    }

    return this.createField({label, className, fieldElement: textAreaElement, error});
};

Sidebar.prototype.createDropdown = function ({
                                                 label,
                                                 help,
                                                 id = '',
                                                 name = '',
                                                 className = '',
                                                 value = '',
                                                 error,
                                                 multiple = false,
                                                 clearable = false,
                                                 searchable = false,
                                                 options = []
                                             } = {}) {
    const selectElement = document.createElement('select');
    selectElement.className = 'ui fluid selection dropdown';
    if (id) {
        selectElement.id = id;
        selectElement.className = `${selectElement.className} ${id}`;
    }
    selectElement.name = name;

    if (searchable) {
        selectElement.className = selectElement.className + ' search';
    }

    if (clearable) {
        selectElement.className = selectElement.className + ' clearable';
    }

    if (multiple) {
        selectElement.className = selectElement.className + ' multiple';
    }

    options.forEach(option => {
        selectElement.insertAdjacentHTML('beforeend', `<option value="${option.value}"${option.value === value ? ' selected' : ''}>${option.name}</option>`);
    });

    return this.createField({label, help, className, fieldElement: selectElement, error});
};

Sidebar.prototype.createCheckboxFields = function (args) {
    return this.createCheckboxes({...args, type: 'checkbox'});
};

Sidebar.prototype.createRadioFields = function (args) {
    return this.createCheckboxes({...args, type: 'radio'});
};

Sidebar.prototype.createCheckboxes = function ({label, name, value, type, options, error, className} = {}) {
    const fields = document.createElement('div');
    fields.className = (className ? className + ' ' : '') + 'grouped fields';

    if (label) {
        const labelElement = document.createElement('label');
        labelElement.setAttribute('for', name);
        labelElement.appendChild(document.createTextNode(label));
        fields.appendChild(labelElement);
    }

    options.forEach(option => {
        const id = `${name}${option.value}`;
        const radioContainer = document.createElement('div');
        radioContainer.className = `ui ${type} checkbox`;

        const inputElement = document.createElement('input');
        inputElement.id = id;
        inputElement.className = 'hidden';
        inputElement.name = name;
        inputElement.type = type;
        inputElement.value = option.value;

        if (value === option.value) {
            radioContainer.className += ' checked';
            inputElement.checked = true;
            inputElement.className = 'checked';
            inputElement.setAttribute('checked', '');
        }

        if (option.disabled) {
            radioContainer.className += ' disabled';
            inputElement.disabled = true;
            inputElement.className = 'disabled';
            inputElement.setAttribute('disabled', '');
        }

        if (option.readOnly) {
            inputElement.readOnly = true;
            inputElement.setAttribute('readOnly', '');
        }

        mxEvent.addListener(inputElement, mxEvent.CHANGE, option.onChange);

        const labelElement = document.createElement('label');
        labelElement.appendChild(document.createTextNode(option.label));
        labelElement.setAttribute('for', id);

        radioContainer.appendChild(inputElement);
        radioContainer.appendChild(labelElement);

        const fieldContainer = document.createElement('div');
        fieldContainer.className = (option.className ? option.className + ' ' : '') + 'field';
        fieldContainer.appendChild(radioContainer);

        if (option.help) {
            fieldContainer.insertAdjacentHTML('beforeend', `<div class="ui left pointing basic large label">${option.help}</div>`);
        }

        fields.appendChild(fieldContainer);
    });

    if (error) {
        const errorField = document.createElement('div');
        errorField.className = fieldErrorClassName;
        errorField.textContent = error;
        fields.appendChild(errorField);
    }

    return fields;
};

Sidebar.prototype.createTable = function (labels = [], columns = []) {
    const headerRow = document.createElement('tr');
    labels.forEach(label => {
        const header = document.createElement('th');
        header.appendChild(document.createTextNode(label));
        headerRow.appendChild(header);
    });

    const headers = document.createElement('thead');
    headers.appendChild(headerRow);

    const dataColumns = document.createElement('tr');
    columns.forEach(column => {
        const data = document.createElement('td');
        data.appendChild(column);
        dataColumns.appendChild(data);
    });

    const body = document.createElement('tbody');
    body.appendChild(dataColumns);

    const tableElement = document.createElement('table');
    tableElement.className = 'ui very basic table';
    tableElement.appendChild(headers);
    tableElement.appendChild(body);
    return tableElement;
};

Sidebar.prototype.clearSelectionState = function () {
    this.selectionState = null;
};

Sidebar.prototype.getSelectionState = function () {
    if (this.selectionState == null) {
        this.selectionState = this.createSelectionState();
    }

    return this.selectionState;
};

Sidebar.prototype.createSelectionState = function () {
    const cells = this.editorUi.editor.graph.getSelectionCells();
    const result = this.initSelectionState();

    for (let i = 0; i < cells.length; i++) {
        this.updateSelectionStateForCell(result, cells[i], cells);
    }

    return result;
};

Sidebar.prototype.initSelectionState = function () {
    return {
        vertices: [], edges: [], x: null, y: null, width: null, height: null, style: {},
        containsImage: false, containsLabel: false, fill: true, glass: true, rounded: true,
        comic: true, autoSize: false, image: true, shadow: true, lineJumps: true
    };
};

Sidebar.prototype.updateSelectionStateForCell = function (result, cell, cells) {
    const graph = this.editorUi.editor.graph;

    if (graph.getModel().isVertex(cell)) {
        result.vertices.push(cell);
        const geo = graph.getCellGeometry(cell);

        if (geo != null) {
            if (geo.width > 0) {
                if (result.width == null) {
                    result.width = geo.width;
                } else if (result.width !== geo.width) {
                    result.width = '';
                }
            } else {
                result.containsLabel = true;
            }

            if (geo.height > 0) {
                if (result.height == null) {
                    result.height = geo.height;
                } else if (result.height !== geo.height) {
                    result.height = '';
                }
            } else {
                result.containsLabel = true;
            }

            if (!geo.relative || geo.offset != null) {
                const x = (geo.relative) ? geo.offset.x : geo.x;
                const y = (geo.relative) ? geo.offset.y : geo.y;

                if (result.x == null) {
                    result.x = x;
                } else if (result.x !== x) {
                    result.x = '';
                }

                if (result.y == null) {
                    result.y = y;
                } else if (result.y !== y) {
                    result.y = '';
                }
            }
        }
    } else if (graph.getModel().isEdge(cell)) {
        result.edges.push(cell);
    }

    const state = graph.view.getState(cell);

    if (state != null) {
        const shape = mxUtils.getValue(state.style, mxConstants.STYLE_SHAPE, null);
        result.containsImage = result.containsImage || shape === 'image';

        for (let key in state.style) {
            const value = state.style[key];

            if (value != null) {
                if (result.style[key] == null) {
                    result.style[key] = value;
                } else if (result.style[key] !== value) {
                    result.style[key] = '';
                }
            }
        }
    }
};
